/**
 * Per-bevel WPS Number + revision accessors for `sections.pass`.
 *
 * Before `wps-history-bevel-scoped` the WPS Number lived at pipe-set
 * scope (`sections.identifiers.wpsNumber`). That meant every bevel
 * shared one WPS Number, and generating a WPS for Bevel 2 produced a
 * PDF named after Bevel 1's WPS#. This module moves the canonical
 * location to per-bevel (one `wpsNumber` + `revision` on each
 * `bevelPasses[]` entry, keyed by the stable `id` from
 * `bevelPassesIdentity`), and provides the get/set helpers
 * downstream slices use.
 *
 * `sections.identifiers.wpsNumber` is retained as a read-only legacy
 * mirror this release — no new writes go there. The one-time copy
 * from legacy into `bevelPasses[0]` is owned by
 * `migrateLegacyIdentifiersIntoPassSection` and runs at the load
 * boundary in `WpsPassDetails`.
 *
 * Pure functions — no React, no IO. All setters return new objects;
 * inputs are never mutated.
 *
 * Added in `wps-history-bevel-scoped` issue 02.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function findEntryIndex(passSection: unknown, bevelId: string): number {
  if (typeof bevelId !== 'string' || bevelId.trim().length === 0) return -1
  if (!isRecord(passSection)) return -1
  const arr = passSection.bevelPasses
  if (!Array.isArray(arr)) return -1
  for (let i = 0; i < arr.length; i += 1) {
    const entry = arr[i]
    if (isRecord(entry) && entry.id === bevelId) return i
  }
  return -1
}

export function getWpsNumberForBevel(
  passSection: unknown,
  bevelId: string
): string {
  const idx = findEntryIndex(passSection, bevelId)
  if (idx < 0 || !isRecord(passSection)) return ''
  const arr = passSection.bevelPasses as unknown[]
  const entry = arr[idx]
  if (!isRecord(entry)) return ''
  return asString(entry.wpsNumber)
}

export function getRevisionForBevel(
  passSection: unknown,
  bevelId: string
): string {
  const idx = findEntryIndex(passSection, bevelId)
  if (idx < 0 || !isRecord(passSection)) return ''
  const arr = passSection.bevelPasses as unknown[]
  const entry = arr[idx]
  if (!isRecord(entry)) return ''
  return asString(entry.revision)
}

function setFieldForBevel<T>(
  passSection: T,
  bevelId: string,
  field: 'wpsNumber' | 'revision',
  value: string
): T {
  if (!isRecord(passSection)) return passSection
  if (!Array.isArray(passSection.bevelPasses)) return passSection
  const idx = findEntryIndex(passSection, bevelId)
  if (idx < 0) return passSection
  const nextArr = (passSection.bevelPasses as unknown[]).map((entry, i) => {
    if (i !== idx) return entry
    if (!isRecord(entry)) return entry
    return { ...entry, [field]: value }
  })
  return { ...passSection, bevelPasses: nextArr } as unknown as T
}

export function setWpsNumberForBevel<T>(
  passSection: T,
  bevelId: string,
  value: string
): T {
  return setFieldForBevel(passSection, bevelId, 'wpsNumber', value)
}

export function setRevisionForBevel<T>(
  passSection: T,
  bevelId: string,
  value: string
): T {
  return setFieldForBevel(passSection, bevelId, 'revision', value)
}

/**
 * One-time copy of pipe-set-scoped `identifiers.{wpsNumber,revision}`
 * into `bevelPasses[0]` for legacy payloads. Idempotent: skips fields
 * that already have a non-empty value on `bevelPasses[0]`. Returns a
 * new section; never mutates.
 */
export function migrateLegacyIdentifiersIntoPassSection<T>(
  passSection: T,
  legacy: { wpsNumber?: string; revision?: string }
): T {
  if (!isRecord(passSection)) return passSection
  if (!Array.isArray(passSection.bevelPasses)) return passSection
  const arr = passSection.bevelPasses as unknown[]
  if (arr.length === 0) return passSection
  const first = arr[0]
  if (!isRecord(first)) return passSection

  const legacyWps = asString(legacy.wpsNumber).trim()
  const legacyRev = asString(legacy.revision).trim()
  const currentWps = asString(first.wpsNumber).trim()
  const currentRev = asString(first.revision).trim()

  const needsWps = legacyWps.length > 0 && currentWps.length === 0
  const needsRev = legacyRev.length > 0 && currentRev.length === 0
  if (!needsWps && !needsRev) return passSection

  const patched: Record<string, unknown> = { ...first }
  if (needsWps) patched.wpsNumber = legacyWps
  if (needsRev) patched.revision = legacyRev
  const nextArr = [patched, ...arr.slice(1)]
  return { ...passSection, bevelPasses: nextArr } as unknown as T
}
